import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Progress,
  Switch,
  Table,
  Upload,
  App as AntApp,
} from "antd";
import { InboxOutlined, UploadOutlined, WarningFilled } from "@ant-design/icons";
import type { RcFile } from "antd/es/upload";

import { releasesApi } from "../api/releases";
import type { AppVersion } from "../api/types";
import { PageHeader } from "../components/PageHeader";
import { Pill } from "../components/Pill";
import { formatDateTimeIST } from "../utils/format";

export function AppReleases() {
  const { notification, modal } = AntApp.useApp();
  const [rows, setRows] = useState<AppVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [apkFile, setApkFile] = useState<RcFile | null>(null);
  const [form] = Form.useForm();

  const formatUploadError = (e: unknown): string => {
    const err = e as {
      code?: string;
      message?: string;
      response?: { status?: number; data?: unknown };
    };
    if (err.code === "ECONNABORTED" || err.code === "ETIMEDOUT") {
      return "Upload timed out. Check your internet connection and try again.";
    }
    if (!err.response) {
      return err.message || "Network error — could not reach the server.";
    }
    const data = err.response.data;
    if (typeof data === "string") return data;
    if (data && typeof data === "object") {
      const obj = data as Record<string, unknown>;
      if (typeof obj.detail === "string") return obj.detail;
      const fieldErrors = Object.entries(obj)
        .map(([k, v]) => {
          const msg = Array.isArray(v) ? v.join(", ") : String(v);
          return `${k}: ${msg}`;
        })
        .filter(Boolean)
        .join(" · ");
      if (fieldErrors) return fieldErrors;
    }
    return `HTTP ${err.response.status ?? "?"} — upload failed.`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await releasesApi.list();
      setRows(res.results);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const latest = rows[0];

  const submit = async () => {
    const vals = await form.validateFields();
    if (!apkFile) {
      notification.warning({ message: "Please select an APK file" });
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    try {
      await releasesApi.upload(
        {
          version: vals.version,
          changelog: vals.changelog || "",
          is_minimum_required: !!vals.is_minimum_required,
          apk_file: apkFile as unknown as File,
        },
        (pct) => setUploadProgress(pct)
      );
      notification.success({ message: "Release uploaded" });
      setUploadOpen(false);
      form.resetFields();
      setApkFile(null);
      setUploadProgress(0);
      load();
    } catch (e: unknown) {
      const msg = formatUploadError(e);
      setUploadError(msg);
      notification.error({ message: "Upload failed", description: msg, duration: 8 });
    } finally {
      setUploading(false);
    }
  };

  const closeUploadModal = () => {
    if (uploading) return;
    setUploadOpen(false);
    setUploadError(null);
    setUploadProgress(0);
  };

  const onForce = (v: AppVersion) => {
    modal.confirm({
      title: `Force users to update to ${v.version}?`,
      content: "All users on older versions will be blocked until they install this build.",
      okButtonProps: { danger: true },
      onOk: async () => {
        await releasesApi.forceUpdate(v.id);
        notification.success({ message: "Minimum version updated" });
        load();
      },
    });
  };

  return (
    <div className="page-shell">
      <PageHeader
        title="App Releases"
        subtitle="Upload new APK builds and control which version employees are required to run."
        right={
          <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>
            Upload New Version
          </Button>
        }
      />

      {latest && (
        <div
          className="card"
          style={{ padding: 24, marginBottom: 24, display: "grid", gridTemplateColumns: "1fr auto", gap: 20 }}
        >
          <div>
            <div className="kpi-label">Current Release</div>
            <div style={{ fontSize: 24, fontWeight: 600, marginTop: 6 }}>{latest.version}</div>
            <div style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 8 }}>
              Released: {formatDateTimeIST(latest.released_at)}
            </div>
            <div className="small-muted mono" style={{ marginTop: 4 }}>
              SHA256: {latest.sha256_checksum.slice(0, 16)}…
            </div>
            <div style={{ fontSize: 13, marginTop: 8 }}>
              Minimum required version: <span className="mono">{latest.minimum_version || "—"}</span>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, alignSelf: "center" }}>
            <Button icon={<UploadOutlined />} href={latest.download_url} target="_blank">
              Download APK
            </Button>
            <Button danger icon={<WarningFilled />} onClick={() => onForce(latest)}>
              Force update (set as minimum)
            </Button>
          </div>
        </div>
      )}

      <Table
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={false}
        columns={[
          { title: "Version", dataIndex: "version", render: (v: string) => <span className="mono">{v}</span> },
          {
            title: "Released",
            dataIndex: "released_at",
            render: (v: string) => formatDateTimeIST(v),
          },
          {
            title: "Status",
            render: (_: unknown, row: AppVersion) => (
              <Pill kind={row.is_active ? (rows[0]?.id === row.id ? "current" : "active") : "archived"} />
            ),
          },
          {
            title: "Minimum?",
            dataIndex: "is_minimum_required",
            render: (v: boolean) =>
              v ? (
                <span style={{ display: "inline-block", width: 10, height: 10, background: "var(--brand)", borderRadius: 999 }} />
              ) : (
                <span className="small-muted">—</span>
              ),
          },
          {
            title: "Downloads",
            dataIndex: "download_count",
            align: "right",
          },
        ]}
      />

      <Modal
        title="Upload New APK"
        open={uploadOpen}
        onCancel={closeUploadModal}
        onOk={submit}
        confirmLoading={uploading}
        okText={uploading ? `Uploading… ${uploadProgress}%` : "Upload Release"}
        okButtonProps={{ disabled: uploading }}
        cancelButtonProps={{ disabled: uploading }}
        maskClosable={!uploading}
        closable={!uploading}
      >
        <Upload.Dragger
          name="file"
          accept=".apk"
          multiple={false}
          disabled={uploading}
          beforeUpload={(file) => {
            setApkFile(file);
            return false;
          }}
          onRemove={() => setApkFile(null)}
          fileList={apkFile ? [apkFile as unknown as import("antd").UploadFile] : []}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Drop your signed APK here or click to browse.</p>
          <p className="ant-upload-hint small-muted">Max 100 MB</p>
        </Upload.Dragger>

        {uploading && (
          <div style={{ marginTop: 16 }}>
            <Progress
              percent={uploadProgress}
              status={uploadProgress < 100 ? "active" : "success"}
              strokeColor={uploadProgress < 100 ? undefined : "#10b981"}
            />
            <div className="small-muted" style={{ marginTop: 4, fontSize: 12 }}>
              {uploadProgress < 100
                ? `Uploading APK… ${uploadProgress}%`
                : "Upload complete — server is processing the file."}
            </div>
          </div>
        )}

        {uploadError && !uploading && (
          <Alert
            type="error"
            showIcon
            message="Upload failed"
            description={uploadError}
            style={{ marginTop: 16 }}
            closable
            onClose={() => setUploadError(null)}
          />
        )}

        <Form layout="vertical" form={form} requiredMark={false} style={{ marginTop: 16 }} disabled={uploading}>
          <Form.Item
            label="Version"
            name="version"
            rules={[{ required: true, pattern: /^\d+\.\d+\.\d+$/, message: "Use semver major.minor.patch" }]}
          >
            <Input placeholder="1.3.0" />
          </Form.Item>
          <Form.Item label="Changelog" name="changelog">
            <Input.TextArea rows={3} placeholder="What changed in this release?" />
          </Form.Item>
          <Form.Item label="Set as minimum required version" name="is_minimum_required" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Alert
            type="error"
            showIcon
            message="Enabling this blocks all users on older versions until they update."
          />
        </Form>
      </Modal>
    </div>
  );
}
